import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useChildProfile } from "../contexts/ChildProfileContext";
import { BigButton, Card } from "../design/BigButton";
import { avatarLabel } from "../lib/assets";
import { UpgradeBanner } from "../components/UpgradeBanner";

export function ParentDashboardShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && ["admin", "content_editor"].includes(user.role);
  return (
    <div className="wmleye-app parent-shell" data-testid="parent-dashboard">
      <aside className="parent-shell__side">
        <h2>Parent Area</h2>
        <p className="parent-shell__me">{user?.email}</p>
        <nav className="parent-shell__nav">
          <NavLink to="/parent/dashboard" end data-testid="pnav-overview">Overview</NavLink>
          <NavLink to="/parent/dashboard/profiles" data-testid="pnav-profiles">Profiles</NavLink>
          <NavLink to="/parent/dashboard/purchases" data-testid="pnav-purchases">Purchases</NavLink>
          <NavLink to="/parent/dashboard/privacy" data-testid="pnav-privacy">Privacy</NavLink>
          <NavLink to="/parent/dashboard/support" data-testid="pnav-support">Support</NavLink>
          {isAdmin && (
            <NavLink to="/admin" data-testid="pnav-admin">🛠 Admin dashboard</NavLink>
          )}
        </nav>
        <div className="parent-shell__foot">
          <BigButton variant="ghost" onClick={() => navigate("/profiles")}>← Back to child</BigButton>
          <BigButton variant="coral" onClick={logout} data-testid="parent-logout">Sign out</BigButton>
        </div>
      </aside>
      <main className="parent-shell__main">
        <UpgradeBanner />
        <Outlet />
      </main>
    </div>
  );
}

export function ParentOverview() {
  const { profiles } = useChildProfile();
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState({});

  useEffect(() => {
    (async () => {
      const map = {};
      for (const p of profiles) {
        try {
          const { data } = await api.get(`/dashboard/${p.id}/summary`);
          map[p.id] = data;
        } catch (_) {}
      }
      setSummaries(map);
    })();
  }, [profiles]);

  const totals = Object.values(summaries).reduce(
    (acc, s) => ({
      stars: acc.stars + (s.stats?.totalStars || 0),
      tokens: acc.tokens + (s.stats?.tokenBalance || 0),
      levels: acc.levels + (s.stats?.levelsCompleted || 0),
      words: acc.words + (s.stats?.wordsEncountered || 0),
    }),
    { stars: 0, tokens: 0, levels: 0, words: 0 },
  );

  return (
    <div className="parent-overview">
      <h1>Overview</h1>
      <div className="parent-overview__stats">
        <Card><h3>Children</h3><p data-testid="stat-children">{profiles.length}</p></Card>
        <Card><h3>Total ⭐</h3><p data-testid="stat-stars">{totals.stars}</p></Card>
        <Card><h3>Tokens</h3><p data-testid="stat-tokens">{totals.tokens}</p></Card>
        <Card><h3>Levels</h3><p data-testid="stat-levels">{totals.levels}</p></Card>
        <Card><h3>Words</h3><p data-testid="stat-words">{totals.words}</p></Card>
      </div>
      <h2>Children</h2>
      <div className="parent-overview__children">
        {profiles.map((p) => {
          const s = summaries[p.id];
          return (
            <Card key={p.id} className="parent-overview__child" data-testid={`child-mini-${p.nickname.toLowerCase()}`}>
              <div className="parent-overview__childHeader">
                <span className="parent-overview__avatar">{avatarLabel(p.avatar)}</span>
                <div><h3>{p.nickname}</h3><p>{p.difficulty} · {p.tokenBalance} 🪙 · {p.totalStars} ⭐</p></div>
              </div>
              <WeeklyBars data={s?.weeklyActivity || []} />
              <BigButton onClick={() => navigate(`/parent/dashboard/progress/${p.id}`)} data-testid={`open-progress-${p.nickname.toLowerCase()}`}>
                View progress →
              </BigButton>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export function WeeklyBars({ data }) {
  const max = Math.max(...data.map((d) => d.minutes), 1);
  return (
    <div className="weekly-bars" role="img" aria-label="Weekly play minutes">
      {data.map((d, i) => (
        <div key={i} className="weekly-bars__col" title={`${d.date}: ${d.minutes} min`}>
          <div className="weekly-bars__bar" style={{ height: `${(d.minutes / max) * 100}%` }} />
          <span>{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}
